import React, { useState } from 'react';
import { B } from '../constants';
import Input from './ui/Input';
import AirportAutocomplete from './AirportAutocomplete';

// Form-only component. Parent owns the search call so it can pass results
// straight into FlightResults / a trip summary.
export default function FlightSearchForm({
  initialOrigin,
  initialDestination,
  initialDepartureDate,
  initialReturnDate,
  onSearch,
  loading,
  error,
}) {
  const [origin, setOrigin] = useState(initialOrigin || null);
  const [destination, setDestination] = useState(initialDestination || null);
  const [departureDate, setDepartureDate] = useState(initialDepartureDate || '');
  const [returnDate, setReturnDate] = useState(initialReturnDate || '');
  const [passengers, setPassengers] = useState(1);
  const [cabinClass, setCabinClass] = useState('ECONOMY');
  const [oneWay, setOneWay] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setLocalError('');
    if (!origin?.iataCode) return setLocalError('Pick an origin airport from the list');
    if (!destination?.iataCode) return setLocalError('Pick a destination airport from the list');
    if (origin.iataCode === destination.iataCode) {
      return setLocalError('Origin and destination must differ');
    }
    if (!departureDate) return setLocalError('Pick a departure date');
    if (!oneWay && returnDate && new Date(returnDate) < new Date(departureDate)) {
      return setLocalError('Return date must be on or after departure');
    }
    onSearch({
      origin: origin.iataCode,
      destination: destination.iataCode,
      departureDate,
      returnDate: !oneWay && returnDate ? returnDate : undefined,
      passengers,
      cabinClass,
    });
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        <AirportAutocomplete
          label="From"
          value={origin}
          onChange={setOrigin}
          placeholder="City or IATA code"
          required
        />
        <AirportAutocomplete
          label="To"
          value={destination}
          onChange={setDestination}
          placeholder="City or IATA code"
          required
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: oneWay ? '1fr 1fr 1fr' : 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        <div>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: B.night }}>Departure</label>
          <Input type="date" min={today} value={departureDate} onChange={(e) => setDepartureDate(e.target.value)} required style={{ background: '#fafafa' }} />
        </div>
        {!oneWay && (
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: B.night }}>Return</label>
            <Input type="date" min={departureDate || today} value={returnDate} onChange={(e) => setReturnDate(e.target.value)} style={{ background: '#fafafa' }} />
          </div>
        )}
        <div>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: B.night }}>Passengers</label>
          <Input type="number" min={1} max={9} value={passengers} onChange={(e) => setPassengers(Math.max(1, Math.min(9, parseInt(e.target.value, 10) || 1)))} style={{ background: '#fafafa' }} />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: B.night }}>Cabin</label>
          <select
            value={cabinClass}
            onChange={(e) => setCabinClass(e.target.value)}
            style={{ width: '100%', padding: '12px 16px', borderRadius: 12, border: '2px solid #e5e0d8', fontSize: 14, fontFamily: "'Montserrat',sans-serif", background: '#fafafa', color: B.night, boxSizing: 'border-box' }}
          >
            <option value="ECONOMY">Economy</option>
            <option value="PREMIUM_ECONOMY">Premium Economy</option>
            <option value="BUSINESS">Business</option>
            <option value="FIRST">First</option>
          </select>
        </div>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: B.muted }}>
        <input
          type="checkbox"
          checked={oneWay}
          onChange={(e) => {
            setOneWay(e.target.checked);
            if (e.target.checked) setReturnDate('');
          }}
        />
        One-way only
      </label>

      {(localError || error) && (
        <div style={{ color: '#dc3545', fontSize: 13 }}>{localError || error}</div>
      )}

      <button
        type="submit"
        disabled={loading}
        style={{
          padding: '14px 20px',
          background: loading ? '#9ca3af' : B.forest,
          color: 'white',
          border: 'none',
          borderRadius: 10,
          fontSize: 15,
          fontWeight: 700,
          cursor: loading ? 'not-allowed' : 'pointer',
          fontFamily: "'Montserrat',sans-serif",
        }}
      >
        {loading ? 'Searching flights…' : 'Search flights'}
      </button>
    </form>
  );
}
